import { useEffect, useState } from 'react'
import { X, ZoomIn, ZoomOut } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useTranslation } from 'react-i18next'

interface ImageViewerModalProps {
  open: boolean
  imageUrl: string
  onClose: () => void
}

export function ImageViewerModal({
  open,
  imageUrl,
  onClose,
}: ImageViewerModalProps) {
  const { t } = useTranslation()
  const [zoom, setZoom] = useState(1)
  const [isDragging, setIsDragging] = useState(false)

  // Reset zoom when opening
  useEffect(() => {
    if (open) {
      setZoom(1)
    }
  }, [open])

  // Keyboard shortcuts
  useEffect(() => {
    if (!open) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      } else if (e.key === '+' || e.key === '=') {
        setZoom((prev) => Math.min(prev + 0.25, 3))
      } else if (e.key === '-') {
        setZoom((prev) => Math.max(prev - 0.25, 0.5))
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open, onClose])

  // Prevent scroll when modal is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = ''
      }
    }
  }, [open])

  if (!open) return null

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.25, 3))
  }

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.25, 0.5))
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    // Only close if clicking the backdrop, not the image
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/98 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200"
      onClick={handleBackdropClick}
    >
      {/* Close Button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={onClose}
        className="absolute top-4 right-4 text-white hover:bg-white/[0.06] z-10 transition-colors"
      >
        <X className="w-6 h-6" />
      </Button>

      {/* Zoom Controls */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-neutral-950/90 border border-white/[0.08] rounded-full px-4 py-2 z-10">
        <Button
          variant="ghost"
          size="icon"
          onClick={handleZoomOut}
          disabled={zoom <= 0.5}
          className="text-white hover:bg-white/[0.06] h-8 w-8 transition-colors"
        >
          <ZoomOut className="w-4 h-4" />
        </Button>
        <span className="text-white text-sm font-medium min-w-[3rem] text-center font-mono">
          {Math.round(zoom * 100)}%
        </span>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleZoomIn}
          disabled={zoom >= 3}
          className="text-white hover:bg-white/[0.06] h-8 w-8 transition-colors"
        >
          <ZoomIn className="w-4 h-4" />
        </Button>
      </div>

      {/* Image */}
      <div
        className="relative max-w-full max-h-full overflow-auto"
        style={{
          cursor: isDragging ? 'grabbing' : zoom > 1 ? 'grab' : 'default',
        }}
        onMouseDown={() => zoom > 1 && setIsDragging(true)}
        onMouseUp={() => setIsDragging(false)}
        onMouseLeave={() => setIsDragging(false)}
      >
        <img
          src={imageUrl}
          alt={t('fullSize')}
          className="max-w-full max-h-[90vh] object-contain select-none transition-transform duration-200"
          style={{
            transform: `scale(${zoom})`,
            transformOrigin: 'center',
          }}
          draggable={false}
        />
      </div>

      {/* Instructions */}
      <div className="absolute bottom-20 left-1/2 -translate-x-1/2 text-neutral-500 text-sm text-center font-mono">
        <p className="hidden sm:block">{t('desktopCloseHint')}</p>
        <p className="sm:hidden">{t('mobileCloseHint')}</p>
      </div>
    </div>
  )
}
